import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useState } from "react";
import styled from "styled-components";

import { gql } from "@examspark/api";
import { assertAppSyncError, getS3FileURL } from "@examspark/common";
import {
  Button,
  Form,
  Modal,
  ModalProps,
  SubmitButtons,
  VSpace,
} from "@examspark/common-ui";
import { IconDownloadLine } from "@examspark/common-ui/dist/components/icons";
import { PaddedBody } from "@examspark/common-ui/dist/components/modals/Modal";
import { ContentExportTypeMeta } from "@examspark/jobs/dist/meta";
import { FeatureFlags, IDSK, JobExportContentFormat } from "@examspark/types";

import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";

const StyledForm = styled.div`
  select {
    font-family: inherit;
    border: solid 1px #ddd;
    height: 3rem;
    border-radius: 0.5rem;
  }
`;

const ExportExamModal = (
  props: ModalProps & {
    examKey?: IDSK;
  },
) => {
  const authContext = useAuthorizationContext();
  const [isCreating, setIsCreating] = useState(false);
  const [format, setFormat] = useState("");
  const [url, setUrl] = useState<string>();
  const flags =
    useFlags<
      Pick<FeatureFlags, "enableQtiV2P1LegacyExport" | "enableQtiV3P0Export">
    >();

  const enabledFormats = Object.assign(
    { "": { label: "Select a format...", enabled: false } },
    ContentExportTypeMeta,
  );

  if (!flags.enableQtiV2P1LegacyExport) {
    delete enabledFormats[JobExportContentFormat.ImsQtiV2P1Legacy];
  }
  if (!flags.enableQtiV3P0Export) {
    delete enabledFormats[JobExportContentFormat.ImsQtiV3P0];
  }

  return (
    <Modal
      title="Export QTI Package"
      size="small"
      onAfterClose={() => setUrl(undefined)}
      buttons={
        <SubmitButtons
          isSubmitting={isCreating}
          isSubmitDisabled={isCreating || !format}
          submitLabel="Generate Export"
          onCancel={props.onRequestClose}
          onSubmit={async () => {
            if (!props.examKey) {
              console.error("No exam key provided");
              return;
            }

            setIsCreating(true);
            try {
              const result = await gql.operations.exam.createExamExport(
                {
                  id: props.examKey.id,
                  format: format as JobExportContentFormat,
                },
                authContext,
              );

              if (!result?.file) throw new Error("No file returned");
              const _url = getS3FileURL(result?.file);
              setUrl(_url);
            } catch (error) {
              alert("An error occurred generating the export.");
              assertAppSyncError(error);
              console.error(error.errors);
            } finally {
              setIsCreating(false);
            }
          }}
        />
      }
      {...props}
    >
      <PaddedBody>
        <VSpace>
          <StyledForm>
            <Form.Field label="Format">
              <select
                disabled={isCreating}
                value={format}
                onChange={(e) => setFormat(e.target.value)}
              >
                {Object.entries(enabledFormats).map(([format, { label }]) => {
                  return (
                    <option key={format} value={format}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </Form.Field>
          </StyledForm>
          {!!url && (
            <Button
              stretch
              disabled={isCreating}
              title={`Download Export`}
              icon={<IconDownloadLine />}
              onClick={() => (window.location.href = url)}
            >
              Download Export
            </Button>
          )}
        </VSpace>
      </PaddedBody>
    </Modal>
  );
};

export default ExportExamModal;
